import { useAuth } from '../../context/AuthContext';
import { ROUTES } from '../../utils/constants';

const Hero = () => {
  const { user } = useAuth();

  return (
    <section className="relative z-10 py-20 md:py-32 bg-white overflow-x-hidden" id="home">
      {/* Blue gradient decorative blobs - more visible */}
      <div className="absolute top-0 left-0 w-64 h-64 md:w-80 md:h-80 lg:w-96 lg:h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
      <div className="absolute top-20 right-0 w-48 h-48 md:w-64 md:h-64 lg:w-80 lg:h-80 bg-primary/25 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-1/4 w-40 h-40 md:w-56 md:h-56 lg:w-72 lg:h-72 bg-blue-400/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-1/3 w-40 h-40 md:w-52 md:h-52 lg:w-64 lg:h-64 bg-indigo-500/25 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left content */}
            <div className="text-center lg:text-right">
              {/* New platform badge */}
              <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary/5 border border-primary/20 text-primary rounded-full mb-8 font-bold text-sm">
                <i className="fas fa-sparkles"></i>
                <span>منصة تعليمية جديدة بالدارجة الجزائرية</span>
              </div>

              {/* Main heading with different approach */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black text-text mb-6 leading-[1.1]">
                ابدأ رحلة التعلم<br />
                <span className="text-primary">بالدارجة الجزائرية</span>
              </h1>

              {/* Enhanced description */}
              <p className="text-xl md:text-2xl text-text2 mb-12 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                منصة تعليمية شاملة بالدارجة الجزائرية - محتوى تفاعلي عصري ومصمم خصيصاً للمتعلمين الجزائريين من كل الأعمار
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-16">
                <a
                  href={user ? ROUTES.COURSES : ROUTES.REGISTER}
                  className="group inline-flex items-center justify-center gap-3 px-8 py-4 md:px-10 md:py-5 bg-gradient-to-r from-primary to-primary-dark text-white font-black text-base md:text-lg rounded-2xl hover:from-primary-dark hover:to-primary transition-all shadow-2xl hover:shadow-primary/50 hover:-translate-y-1"
                >
                  <span className="drop-shadow">ابدأ التعلم درك</span>
                  <i className="fas fa-arrow-left group-hover:-translate-x-1 transition-transform drop-shadow"></i>
                </a>
                <a
                  href="#categories"
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 md:px-10 md:py-5 bg-white text-text font-bold text-base md:text-lg rounded-2xl border-2 border-gray-200 hover:border-primary hover:text-primary transition-all shadow-lg hover:shadow-xl"
                >
                  <i className="fas fa-book-open"></i>
                  <span>تعرف علينا أكثر</span>
                </a>
              </div>

              {/* Feature highlights - real value props */}
              <div className="grid grid-cols-3 gap-2 md:gap-4 max-w-2xl mx-auto lg:mx-0">
                <div className="flex flex-col items-center gap-2 justify-center p-3 md:p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="w-8 h-8 md:w-10 md:h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-graduation-cap text-primary text-sm md:text-base"></i>
                  </div>
                  <div className="text-center lg:text-right">
                    <div className="font-bold text-text text-xs md:text-sm">محتوى تعليمي</div>
                    <div className="text-[10px] md:text-xs text-text3">شامل ومنظم</div>
                  </div>
                </div>

                <div className="flex flex-col items-center gap-2 justify-center p-3 md:p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="w-8 h-8 md:w-10 md:h-10 bg-secondary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-play-circle text-secondary text-sm md:text-base"></i>
                  </div>
                  <div className="text-center lg:text-right">
                    <div className="font-bold text-text text-xs md:text-sm">تفاعلي ومرئي</div>
                    <div className="text-[10px] md:text-xs text-text3">ممتع وجذاب</div>
                  </div>
                </div>

                <div className="flex flex-col items-center gap-2 justify-center p-3 md:p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="w-8 h-8 md:w-10 md:h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-users text-accent text-sm md:text-base"></i>
                  </div>
                  <div className="text-center lg:text-right">
                    <div className="font-bold text-text text-xs md:text-sm">لكل الأعمار</div>
                    <div className="text-[10px] md:text-xs text-text3">أطفال وكبار</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right side - Visual element */}
            <div className="relative">
              <div className="relative max-w-[600px] mx-auto">
                {/* Main card with course preview */}
                <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
                  {/* Header */}
                  <div className="bg-gradient-to-br from-primary to-primary-dark p-5 md:p-6 text-white">
                    <div className="flex items-center gap-3 mb-3 md:mb-4">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-white/30 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg">
                        <i className="fas fa-graduation-cap text-xl md:text-2xl drop-shadow-lg"></i>
                      </div>
                      <div>
                        <h3 className="font-black text-base md:text-lg drop-shadow-md">ابدأ التعلم الآن</h3>
                        <p className="text-xs md:text-sm text-white/95 drop-shadow">تعلم بطريقة عصرية وممتعة</p>
                      </div>
                    </div>
                  </div>

                  {/* Content preview */}
                  <div className="p-6 space-y-4">
                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                      <div className="w-14 h-14 bg-blue-500 rounded-xl flex items-center justify-center text-white text-xl flex-shrink-0">
                        <i className="fas fa-language"></i>
                      </div>
                      <div className="flex-1 text-right">
                        <h4 className="font-bold text-text mb-1">الإنجليزية للجزائريين</h4>
                        <p className="text-sm text-text3">مسار تعليمي متكامل</p>
                      </div>
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                        <i className="fas fa-arrow-left text-primary text-sm"></i>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                      <div className="w-14 h-14 bg-green-500 rounded-xl flex items-center justify-center text-white text-xl flex-shrink-0">
                        <i className="fas fa-calculator"></i>
                      </div>
                      <div className="flex-1 text-right">
                        <h4 className="font-bold text-text mb-1">علم ابني الأرقام</h4>
                        <p className="text-sm text-text3">مسار تعليمي متكامل</p>
                      </div>
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                        <i className="fas fa-arrow-left text-primary text-sm"></i>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100 opacity-50">
                      <div className="w-14 h-14 bg-purple-500 rounded-xl flex items-center justify-center text-white text-xl flex-shrink-0">
                        <i className="fas fa-book"></i>
                      </div>
                      <div className="flex-1 text-right">
                        <h4 className="font-bold text-text mb-1">المزيد قريباً...</h4>
                        <p className="text-sm text-text3">محتوى جديد كل أسبوع</p>
                      </div>
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                        <i className="fas fa-lock text-gray-400 text-sm"></i>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating badge */}
                <div className="absolute -top-6 -right-6 bg-accent text-white px-6 py-3 rounded-2xl shadow-xl font-bold text-sm rotate-3 hidden md:block">
                  <div className="flex items-center gap-2">
                    <i className="fas fa-star"></i>
                    <span>محتوى حصري!</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
